import { Settings } from "./settings";

/**
 * Midnight Alchemy — Symbol Data
 *
 * Indices are the client ids the Dmon provider hands the game (0–10).
 *
 *   0     wild         wild
 *   1     hp1          high pay — philosopher's stone
 *   2-4   mp2 mp3 mp1  mid pays — closed book, crystal orb, hourglass
 *   5-8   lp1-lp4      low pays — red, blue, green, purple potion
 *   9     sc1          scatter — sun
 *   10    sc2          scatter — moon
 *
 * Spines: one rig per symbol in spines/reel_symbol/, default skin only,
 * 185 × 185 setup box. Clips:
 *   lp1-lp4               idle / win
 *   hp1 mp1-mp3 sc1 sc2   land / idle / win
 *   wild                  land / idle / win / appear
 */
export const symbolData = {
  0: { name: "wild", spine: "wild", symbolPay: "wild", texture: "wild", value: null },
  1: { name: "hp1", spine: "hp1", symbolPay: "hp", texture: "hp1", value: null },
  2: { name: "mp2", spine: "mp2", symbolPay: "mp", texture: "mp2", value: null },
  3: { name: "mp3", spine: "mp3", symbolPay: "mp", texture: "mp3", value: null },
  4: { name: "mp1", spine: "mp1", symbolPay: "mp", texture: "mp1", value: null },
  5: { name: "lp1", spine: "lp1", symbolPay: "lp", texture: "lp1", value: null },
  6: { name: "lp2", spine: "lp2", symbolPay: "lp", texture: "lp2", value: null },
  7: { name: "lp3", spine: "lp3", symbolPay: "lp", texture: "lp3", value: null },
  8: { name: "lp4", spine: "lp4", symbolPay: "lp", texture: "lp4", value: null },
  9: { name: "sc1", spine: "sc1", symbolPay: "scatter", texture: "sc1", value: null },
  10: { name: "sc2", spine: "sc2", symbolPay: "scatter", texture: "sc2", value: null },
};

// ─── Derived Constants ───

export const WILD_SYMBOL_ID = 0;
export const HIGH_PAY_SYMBOL_ID = 1;

export const SCATTER_SYMBOL_ID = 9;
export const SCATTER_SYMBOL_IDS = [9, 10];

// ─── What may appear on a SPINNING reel ───
//
// The blur is cosmetic — the API decides what lands — but it must never show a
// symbol the math cannot produce.
//
//   0-8    paying symbols    yes
//   9-10   scatters          no — shown when they land
//
// Derived from symbolData, so a new symbol is EXCLUDED until someone states
// that it may spin.
const SPIN_FILLER_ALLOWED = new Set([0, 1, 2, 3, 4, 5, 6, 7, 8]);

export const SPIN_FILLER_SYMBOL_IDS = Object.entries(symbolData)
  .filter(([id, data]) => data?.texture && SPIN_FILLER_ALLOWED.has(Number(id)))
  .map(([id]) => Number(id));

// ─── Not in this game ───
//
// Cleopatra's Fire's, Mad Labs' and Zeus's symbols. Midnight Alchemy has none of
// them, so each is an explicit ABSENCE — null / [] / {} — rather than a value.
//
// They cannot simply be deleted: this build treats a missing named export as a
// webpack ERROR, even when the importing file is a feature this game never
// renders. An entry lives here until its last IMPORTER is deleted.
//
// The sentinels are also the safer answer: `id === null` is false for every real
// symbol, while a deleted export reads as `undefined`, and `undefined ===
// undefined` is TRUE — a false positive waiting to happen.
//
// Consume them defensively — `?? []`, `?? null`, `?.` — and never call a method
// on one at MODULE level.

// Cleopatra's Fire (coin, Hold & Win)
export const COIN_SYMBOL_ID = null;
export const COIN_JACKPOT_SYMBOL_ID = null;
// [], not null: read at module load (COIN_SYMBOL_IDS[0], .includes).
export const COIN_SYMBOL_IDS = [];
// null, not 0: 0 is the wild.
export const BLANK_SYMBOL_ID = null;
export const CHEST_SYMBOL_ID = null;
export const GOLDEN_CHEST_SYMBOL_ID = null;
export const SCARAB_SYMBOL_ID = null;
export const JACKPOT_NAME_TO_SKIN = {};

// Mad Labs
export const MYSTERY_SYMBOL_ID = null;
export const GOLDEN_FRAME_SYMBOL_ID = null;
export const BONUS_SYMBOL_ID = null;
export const BONUS_SYMBOL_IDS = [];
// Null although the game has two scatters: these drive Mad Labs reveal code.
export const SCATTER_1_SYMBOL_ID = null;
export const SCATTER_2_SYMBOL_ID = null;
export const SCATTER_3_SYMBOL_ID = null;
export const SCATTER_BLANK_SYMBOL_ID = null;
export const GAMBLE_LOW_SYMBOL_ID = null;
export const GAMBLE_MID_SYMBOL_ID = null;
export const GAMBLE_HIGH_SYMBOL_ID = null;
export const COLLECT_SYMBOL_ID = null;
export const MULTIPLIER_SYMBOL_ID = null;
export const MULTIPLIER_VALUE_TO_SKIN = {};

// Zeus
export const ROYAL_SYMBOL_ID = null;
export const JACKPOT_SYMBOL_IDS = [];
export const ZEUS_DOUBLE_ID = null;
export const ZEUS_MYSTERY_ID = null;
export const ZEUS_COLLECT_ID = null;
export const CD_MULT_ID = null;
export const CM_MINOR_ID = null;
export const CM_MINI_ID = null;
export const CM_MAJOR_ID = null;
export const JACKPOT_VALUE_TO_ID = {};

// ─── Helper Functions ───

export const isCoinSymbol = (id) => COIN_SYMBOL_IDS.includes(id);
export const isBonusSymbol = () => false;
export const isMysterySymbol = () => false;
export const isScatterSymbol = (id) => SCATTER_SYMBOL_IDS.includes(id);
export const isJackpotSymbol = () => false;
export const isRoyalSymbol = () => false;
export const getJackpotTier = () => null;

export const getCoinValue = (id) => {
  const sym = symbolData[id];
  return sym?.value ?? null;
};

/**
 * Base-game render priority, keyed by symbolPay so this stays data-driven —
 * add a symbol to symbolData and it inherits the right layer automatically.
 *
 * Symbols within one category deliberately share a value. Ties fall back to
 * natural left-to-right reel order.
 */
const BASE_PAY_Z_INDEX = {
  scatter: 9,
  wild: 8,
  hp: 7,
  mp: 6,
  lp: 5,
};

/**
 * Overlay z-index for a flat overlay — one container, every cell a sibling.
 * Higher value renders on top. PIXI only sorts among siblings, which is why
 * the overlay is flat in the first place.
 *
 *   scatter(9) > wild(8) > high pay(7) > mid pay(6) > low pay(5) > everything else(1)
 */
export const getSymbolZIndex = (id) => {
  if (isCoinSymbol(id)) return 60;

  const basePayZIndex = BASE_PAY_Z_INDEX[symbolData[id]?.symbolPay];
  if (basePayZIndex !== undefined) return basePayZIndex;

  return 1;
};

// Low-pay symbol IDs — for the default slotface
export const LOW_PAY_SYMBOL_IDS = Object.entries(symbolData)
  .filter(([, d]) => d.symbolPay === "lp")
  .map(([id]) => parseInt(id));

/**
 * Low-pay ids that can NOT drop in free spins. The game has no free spins, so
 * none.
 */
export const convertedLowSymbolIds = () => [];

/**
 * Default slotface — a low-pay grid so nothing pays on load.
 */
export const DEFAULT_SLOTFACE = (() => {
  const { REEL_COUNT, SYMBOLS_VISIBLE } = Settings;
  const lp = LOW_PAY_SYMBOL_IDS;
  return Array.from({ length: REEL_COUNT }, (_, ri) =>
    Array.from(
      { length: SYMBOLS_VISIBLE },
      (_, si) => lp[(ri + si) % lp.length],
    ),
  );
})();
